import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  ColumnDef,
  SortingState,
  flexRender
} from '@tanstack/react-table';
import { Table } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faArrowRight,
  faAngleRight
} from '@fortawesome/free-solid-svg-icons';
import { styles, scrollbarStyles } from './styles/tableStyles';
import PhoenixLoader from 'components/common/PhoenixLoader';

interface TableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  itemsPerPage?: number;
  isLoading?: boolean;
}

const GTable = <T,>({
  data,
  columns,
  itemsPerPage = 6,
  isLoading = false
}: TableProps<T>) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [isViewAll, setIsViewAll] = React.useState<boolean>(false);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: itemsPerPage
      }
    }
  });

  const handleViewToggle = () => {
    if (isViewAll) {
      table.setPageSize(itemsPerPage);
      setIsViewAll(false);
    } else {
      table.setPageSize(data.length);
      setIsViewAll(true);
    }
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="dark-table-container" style={styles.tableContainer}>
        {isLoading ? (
          <PhoenixLoader />
        ) : (
          <>
            <div className="table-wrapper" style={styles.tableWrapper}>
              <Table className="dark-table" responsive>
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          className="table-header"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <span style={styles.sortIcon}>
                            <FontAwesomeIcon
                              icon={
                                header.column.getIsSorted() === 'asc'
                                  ? faSortUp
                                  : header.column.getIsSorted() === 'desc'
                                  ? faSortDown
                                  : faSort
                              }
                              className="fs--2"
                            />
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length > 0 ? (
                    table.getRowModel().rows.map(row => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length} style={styles.noData}>
                        No results found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {table.getPageCount() > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="mb-0 d-none d-sm-block me-3 fw-semi-bold text-900 fs-9">
                  <span className="text-600">
                    {table.getState().pagination.pageIndex * itemsPerPage + 1}{' '}
                    to{' '}
                    {isViewAll
                      ? data.length
                      : Math.min(
                          (table.getState().pagination.pageIndex + 1) *
                            itemsPerPage,
                          data.length
                        )}{' '}
                    items of {data.length}
                  </span>
                  <Button
                    variant="link"
                    className="ms-2 p-0"
                    onClick={handleViewToggle}
                    disabled={data.length <= itemsPerPage}
                  >
                    {isViewAll ? (
                      <>
                        View less{' '}
                        <FontAwesomeIcon icon={faAngleRight} className="ms-1" />
                      </>
                    ) : (
                      <>
                        View all{' '}
                        <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                      </>
                    )}
                  </Button>
                </div>
                {!isViewAll && table.getPageCount() > 0 && (
                  <div style={styles.pagination}>
                    <button
                      style={{
                        ...styles.pageButton,
                        opacity: !table.getCanPreviousPage() ? 0.5 : 1
                      }}
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} className="fs-10" />
                    </button>
                    {table.getPageCount() > 10 ? (
                      <>
                        {[...Array(3)].map((_, i) => (
                          <button
                            key={i}
                            style={{
                              ...styles.pageButton,
                              backgroundColor:
                                table.getState().pagination.pageIndex === i
                                  ? '#3b82f6'
                                  : 'transparent'
                            }}
                            onClick={() => table.setPageIndex(i)}
                          >
                            {i + 1}
                          </button>
                        ))}
                        <span style={styles.pageButton}>...</span>
                        {[...Array(3)].map((_, i) => {
                          const pageIndex = table.getPageCount() - 3 + i;
                          return (
                            <button
                              key={pageIndex}
                              style={{
                                ...styles.pageButton,
                                backgroundColor:
                                  table.getState().pagination.pageIndex ===
                                  pageIndex
                                    ? '#3b82f6'
                                    : 'transparent'
                              }}
                              onClick={() => table.setPageIndex(pageIndex)}
                            >
                              {pageIndex + 1}
                            </button>
                          );
                        })}
                      </>
                    ) : (
                      Array.from({ length: table.getPageCount() }, (_, i) => (
                        <button
                          key={i}
                          style={{
                            ...styles.pageButton,
                            backgroundColor:
                              table.getState().pagination.pageIndex === i
                                ? '#3b82f6'
                                : 'transparent'
                          }}
                          onClick={() => table.setPageIndex(i)}
                        >
                          {i + 1}
                        </button>
                      ))
                    )}
                    <button
                      style={{
                        ...styles.pageButton,
                        opacity: !table.getCanNextPage() ? 0.5 : 1
                      }}
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="fs-10"
                      />
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default GTable;
